import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Loader2, Save, Activity, Pause, TrendingUp } from 'lucide-react';
import { adminCompanies, adminSetPrice, adminToggleAutoPrice } from '../../api';

export default function PriceControl({ token }) {
  const [companies, setCompanies] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const c = await adminCompanies(token);
      setCompanies(c);
      const d = {};
      c.forEach((co) => {
        d[co.id] = {
          lastPrice: co.lastPrice ?? '',
          change: co.change ?? 0,
        };
      });
      setDrafts(d);
    } catch {
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setField = (id, field, value) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const save = async (co) => {
    const d = drafts[co.id];
    const price = parseFloat(d.lastPrice);
    if (!price || price <= 0) {
      toast.error('Enter a valid price greater than 0');
      return;
    }
    setSavingId(co.id);
    try {
      await adminSetPrice(token, co.id, {
        lastPrice: price,
        change: d.change === '' || d.change === undefined ? 0 : parseFloat(d.change),
      });
      toast.success(`${co.ticker} price set to $${price.toFixed(2)}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update price');
    } finally {
      setSavingId(null);
    }
  };

  const toggleAuto = async (co) => {
    try {
      const next = !(co.autoPrice !== false);
      await adminToggleAutoPrice(token, co.id, next);
      toast.success(next ? `${co.ticker} auto-ticking resumed` : `${co.ticker} price frozen`);
      await load();
    } catch {
      toast.error('Failed to toggle auto-update');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-20 text-[var(--muted)]">
        <Loader2 size={26} className="mx-auto text-[var(--orange)] animate-spin" />
        <p className="mt-3 text-[13px]">Loading prices...</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-[var(--panel)] border border-[var(--border)] overflow-hidden" data-testid="price-control-panel">
      <div className="px-5 py-4 border-b border-[var(--border)] flex items-center gap-2.5">
        <TrendingUp size={18} className="text-[var(--orange)]" />
        <div>
          <div className="text-[14px] font-semibold text-white">LIVE PRICE CONTROL</div>
          <div className="text-[11px] text-[var(--muted-2)] mt-0.5">Set a price manually to freeze it, or resume auto-ticking. Locked companies (SkyWay) can't be changed here.</div>
        </div>
      </div>
      <div className="divide-y divide-[var(--border)]">
        {companies.map((co) => {
          const d = drafts[co.id] || {};
          const isAuto = co.autoPrice !== false && !co.locked;
          const draftPrice = parseFloat(d.lastPrice);
          const changed = !Number.isNaN(draftPrice) && (draftPrice !== co.lastPrice || parseFloat(d.change) !== co.change);
          return (
            <div key={co.id} className="px-5 py-4 flex flex-wrap items-center gap-4" data-testid={`price-row-${co.id}`}>
              <div className="flex items-center gap-3 min-w-[190px] flex-1">
                <div className="w-9 h-9 rounded-lg bg-[var(--bg)] border border-[var(--border)] flex items-center justify-center overflow-hidden">
                  {co.logo ? (
                    <img src={co.logo} alt={co.ticker} className="w-full h-full object-contain" />
                  ) : (
                    <span className="text-[11px] font-mono text-[var(--muted)]">{(co.ticker || '?').slice(0, 3)}</span>
                  )}
                </div>
                <div className="leading-tight">
                  <div className="text-[13px] font-semibold text-white">{co.name}</div>
                  <div className="text-[11px] font-mono text-[var(--muted-2)]">{co.ticker}</div>
                </div>
              </div>

              <div>
                <label className="block text-[9px] font-mono tracking-widest text-[var(--muted-2)] mb-1">PRICE ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={d.lastPrice}
                  disabled={co.locked}
                  onChange={(e) => setField(co.id, 'lastPrice', e.target.value)}
                  data-testid={`price-input-${co.id}`}
                  className="w-28 bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2 text-[13px] font-mono text-white focus:outline-none focus:border-[var(--orange)] disabled:opacity-40"
                />
              </div>

              <div>
                <label className="block text-[9px] font-mono tracking-widest text-[var(--muted-2)] mb-1">CHANGE %</label>
                <input
                  type="number"
                  step="0.01"
                  value={d.change}
                  disabled={co.locked}
                  onChange={(e) => setField(co.id, 'change', e.target.value)}
                  data-testid={`change-input-${co.id}`}
                  className="w-24 bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2 text-[13px] font-mono text-white focus:outline-none focus:border-[var(--orange)] disabled:opacity-40"
                />
              </div>

              <button
                onClick={() => save(co)}
                disabled={co.locked || savingId === co.id || !changed}
                data-testid={`save-price-${co.id}`}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[var(--orange)] text-white text-[12px] font-semibold hover:bg-[var(--orange-bright)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {savingId === co.id ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} SAVE
              </button>

              {co.locked ? (
                <span className="text-[11px] font-mono text-[var(--muted-2)] px-3 py-1.5 rounded-md border border-[var(--border)]">LOCKED</span>
              ) : (
                <button
                  onClick={() => toggleAuto(co)}
                  data-testid={`auto-toggle-${co.id}`}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold border transition-colors ${
                    isAuto
                      ? 'border-[var(--green)] text-[var(--green)] bg-[rgba(22,199,132,0.08)]'
                      : 'border-[var(--border)] text-[var(--muted)] hover:text-white'
                  }`}
                >
                  {isAuto ? <><Activity size={13} /> AUTO ON</> : <><Pause size={13} /> FROZEN</>}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
